import notifyActionOutcome from "./actionOutcomeNotification";
import { ActionOutcome } from "./actionVerification";

/**
 * One voice for "your command did not happen, and here is why".
 *
 * Four places on the click→wire path can refuse a command: an action with no
 * bound message, a build with no connection mode, no selected bank, and no
 * socket willing to claim the frame. Each used to `return` silently, so a
 * refused command was indistinguishable from a delivered one — which is how
 * "actions never reach the vendor" reached several sites before anyone could
 * describe the symptom.
 *
 * They share this helper rather than each calling `notification.warning`
 * directly so the four read as one behaviour: same severity, same placement,
 * same dwell time. Four hand-rolled copies had already started to drift on the
 * theme class before this existed.
 *
 * This IS `ActionOutcome.Blocked`'s presentation — it renders through
 * `notifyActionOutcome`, so the refusal carries the same glyph and colour class
 * as the other three outcomes and the four stay visibly distinct. The specific
 * cause is kept as the heading rather than replaced by the generic "Not sent",
 * because naming the cause is what makes the notification actionable.
 *
 * WARNING, not ERROR, on purpose: nothing failed. The system correctly declined
 * to send something it could not address, and the operator's next step is to fix
 * configuration or selection, not to report a fault.
 *
 * Deliberately NOT gated on the user's `commandNotifications` preference. That
 * setting silences routine per-command chatter; these messages mean the click
 * did nothing at all, and hiding them behind a preference would restore the
 * silent failure this exists to end.
 */
export default function notifyCommandBlocked(
  message: string,
  description: string,
  theme?: string,
): void {
  notifyActionOutcome({
    outcome: ActionOutcome.Blocked,
    title: message,
    detail: description,
    theme,
  });
}
